/**
 * Hybrid logical clock. Timestamps are fixed-width strings so that plain
 * lexicographic comparison (JS `<`, Postgres `COLLATE "C"`) orders them by
 * (wall time, counter, node id) — giving a total, deterministic order.
 */
export const HLC_ZERO = '';

const WALL_WIDTH = 15;
const COUNTER_WIDTH = 6;
const MAX_COUNTER = 10 ** COUNTER_WIDTH - 1;

export interface HlcState {
  wall: number;
  counter: number;
}

export interface ParsedHlc {
  wall: number;
  counter: number;
  node: string;
}

export function formatHlc(wall: number, counter: number, node: string): string {
  return `${String(wall).padStart(WALL_WIDTH, '0')}.${String(counter).padStart(COUNTER_WIDTH, '0')}.${node}`;
}

export function parseHlc(hlc: string): ParsedHlc | null {
  const m = /^(\d{15})\.(\d{6})\.(.+)$/.exec(hlc);
  if (!m) return null;
  return { wall: Number(m[1]), counter: Number(m[2]), node: m[3]! };
}

export function maxHlc(...values: string[]): string {
  let best = HLC_ZERO;
  for (const v of values) if (v > best) best = v;
  return best;
}

export class HybridClock {
  private wall = 0;
  private counter = 0;

  constructor(
    readonly node: string,
    state?: HlcState,
    private readonly now: () => number = Date.now,
  ) {
    if (!node || node.includes('.')) throw new Error('HLC node id must be non-empty and contain no dots');
    if (state) {
      this.wall = state.wall;
      this.counter = state.counter;
    }
  }

  /** Produce a new timestamp strictly greater than every one issued or observed. */
  tick(): string {
    const t = this.now();
    if (t > this.wall) {
      this.wall = t;
      this.counter = 0;
    } else {
      this.bump();
    }
    return formatHlc(this.wall, this.counter, this.node);
  }

  /** Merge a timestamp received from another node so later ticks sort after it. */
  observe(hlc: string): void {
    const p = parseHlc(hlc);
    if (!p) return;
    if (p.wall > this.wall) {
      this.wall = p.wall;
      this.counter = p.counter;
    } else if (p.wall === this.wall && p.counter > this.counter) {
      this.counter = p.counter;
    }
  }

  state(): HlcState {
    return { wall: this.wall, counter: this.counter };
  }

  private bump() {
    if (this.counter >= MAX_COUNTER) {
      this.wall += 1;
      this.counter = 0;
    } else {
      this.counter += 1;
    }
  }
}
